import { useEffect, useMemo, useRef, useState } from "react";
import type { JSX, SyntheticEvent } from "react";
import type { ShoppingCategory, ShoppingItem, ShoppingList, ShoppingListSummary, ShoppingSuggestion } from "../../shared/shopping-list";
import { sortShoppingItems } from "../../shared/shopping-list";
import {
  addShoppingItem,
  bulkImportShoppingItems,
  clearCompletedShoppingItems,
  completeShoppingItem,
  createShoppingList,
  deleteShoppingCategory,
  fetchShoppingList,
  fetchShoppingLists,
  reorderShoppingCategories,
  suggestShoppingItems
} from "../api/shopping-list";

type ShoppingListModuleProps = {
  pathname: string;
  onNavigate: (path: string) => void;
};

export function ShoppingListModule({ pathname, onNavigate }: ShoppingListModuleProps): JSX.Element {
  const [lists, setLists] = useState<ShoppingListSummary[]>([]);
  const [activeList, setActiveList] = useState<ShoppingList | undefined>(undefined);
  const [newListName, setNewListName] = useState("Weekly shop");
  const [itemName, setItemName] = useState("");
  const [quantityText, setQuantityText] = useState("");
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkImportText, setBulkImportText] = useState("");
  const [isManagingCategories, setIsManagingCategories] = useState(false);
  const [suggestions, setSuggestions] = useState<ShoppingSuggestion[]>([]);
  const [syncMessage, setSyncMessage] = useState("Synced");
  const [error, setError] = useState<string | undefined>(undefined);

  const itemNameInputRef = useRef<HTMLInputElement>(null);
  const isAllListsView = pathname === "/shopping-lists/all";
  const activeListId = !isAllListsView ? /^\/shopping-lists\/([^/]+)/.exec(pathname)?.[1] : undefined;

  useEffect(() => {
    if (isAddingItem) {
      itemNameInputRef.current?.focus();
    }
  }, [isAddingItem]);

  useEffect(() => {
    let active = true;
    void fetchShoppingLists()
      .then(async (loaded) => {
        if (!active) {
          return;
        }
        setLists(loaded);
        if (isAllListsView) {
          setActiveList(undefined);
          return;
        }
        if (activeListId !== undefined) {
          const list = await fetchShoppingList(activeListId);
          setActiveList(list);
          setCategoryId(list.categories[0]?.id);
          return;
        }

        if (loaded.length > 0) {
          const list = await fetchShoppingList(loaded[0].id);
          setActiveList(list);
          setCategoryId(list.categories[0]?.id);
          onNavigate(`/shopping-lists/${list.id}`);
        }
      })
      .catch(() => {
        if (active) {
          setError("Could not load shopping lists. Offline changes will be added when local sync is available.");
          setSyncMessage("Offline");
        }
      });
    return () => {
      active = false;
    };
  }, [activeListId, isAllListsView, onNavigate]);

  useEffect(() => {
    let active = true;
    if (itemName.trim().length < 2) {
      setSuggestions([]);
      return () => {
        active = false;
      };
    }

    void suggestShoppingItems(itemName).then((items) => {
      if (active) {
        setSuggestions(items);
      }
    });
    return () => {
      active = false;
    };
  }, [itemName]);

  const groupedItems = useMemo(() => {
    if (activeList === undefined) {
      return [];
    }
    return activeList.categories
      .map((category) => ({
        category,
        items: sortShoppingItems(activeList.items.filter((item) => item.categoryId === category.id)),
        incompleteItemCount: activeList.items.filter((item) => item.categoryId === category.id && !item.completed).length
      }))
      .filter(({ incompleteItemCount }) => isManagingCategories || incompleteItemCount > 0);
  }, [activeList, isManagingCategories]);

  async function refreshList(listId = activeList?.id): Promise<ShoppingList | undefined> {
    /* v8 ignore next 3 -- refresh is only called after a list has been selected or created. */
    if (listId === undefined) {
      return undefined;
    }
    const list = await fetchShoppingList(listId);
    setActiveList(list);
    setLists(await fetchShoppingLists());
    setSyncMessage("Synced");
    return list;
  }

  async function onCreateList(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(undefined);
    try {
      const list = await createShoppingList(newListName);
      setLists([list, ...lists]);
      setActiveList(list);
      setCategoryId(list.categories[0]?.id);
      setNewListName("");
      onNavigate(`/shopping-lists/${list.id}`);
      setSyncMessage("Synced");
    } catch {
      setError("Could not create list.");
    }
  }

  async function onAddItem(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    /* v8 ignore next 3 -- the add form is only submitted with an active list and non-empty item. */
    if (activeList === undefined || itemName.trim().length === 0) {
      return;
    }
    const categoryName = categoryId === "__new__" ? newCategoryName.trim() : undefined;
    if (categoryId === "__new__" && (categoryName === undefined || categoryName.length === 0)) {
      setError("Enter a category name before adding the item.");
      return;
    }
    setSyncMessage("Syncing…");
    try {
      await addShoppingItem(activeList.id, {
        displayName: itemName,
        quantityText: quantityText.trim() || undefined,
        categoryId: categoryId === "__new__" ? undefined : categoryId,
        categoryName,
        clientMutationId: crypto.randomUUID()
      });
      setItemName("");
      setQuantityText("");
      setSuggestions([]);
      setNewCategoryName("");
      setIsAddingItem(false);
      const list = await refreshList(activeList.id);
      if (categoryName !== undefined) {
        setCategoryId(list?.categories.find((category) => category.name.toLocaleLowerCase() === categoryName.toLocaleLowerCase())?.id);
      }
    } catch {
      setError("Could not add item. Try again when you are back online.");
      setSyncMessage("Sync error");
    }
  }

  async function onBulkImport(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (activeList === undefined || bulkImportText.trim().length === 0) {
      return;
    }
    setError(undefined);
    setSyncMessage("Syncing…");
    try {
      const result = await bulkImportShoppingItems(activeList.id, bulkImportText);
      setBulkImportText("");
      setIsBulkImporting(false);
      setSyncMessage(`Imported ${String(result.items.length)} item${result.items.length === 1 ? "" : "s"}`);
      await refreshList(activeList.id);
    } catch {
      setError("Could not bulk import items. Try again when you are back online.");
      setSyncMessage("Sync error");
    }
  }

  async function onToggleItem(item: ShoppingItem): Promise<void> {
    /* v8 ignore next 3 -- item rows are only rendered once an active list exists. */
    if (activeList === undefined) {
      return;
    }
    setActiveList({
      ...activeList,
      items: activeList.items.map((candidate) => candidate.id === item.id ? { ...candidate, completed: !candidate.completed } : candidate)
    });
    try {
      await completeShoppingItem(activeList.id, item.id, !item.completed);
      await refreshList(activeList.id);
    } catch {
      setError("Could not sync item completion.");
      await refreshList(activeList.id);
    }
  }

  async function onClearCompleted(): Promise<void> {
    /* v8 ignore next 3 -- the clear button is only rendered once an active list exists. */
    if (activeList === undefined) {
      return;
    }
    await clearCompletedShoppingItems(activeList.id);
    await refreshList(activeList.id);
  }

  async function onDeleteCategory(category: ShoppingCategory): Promise<void> {
    /* v8 ignore next 3 -- delete controls are only rendered once an active list exists. */
    if (activeList === undefined) {
      return;
    }
    setSyncMessage("Syncing…");
    try {
      await deleteShoppingCategory(activeList.id, category.id);
      if (categoryId === category.id) {
        setCategoryId(activeList.categories.find((candidate) => candidate.id !== category.id)?.id);
      }
      await refreshList(activeList.id);
    } catch {
      setError("Could not delete category.");
      setSyncMessage("Sync error");
    }
  }

  async function moveCategory(category: ShoppingCategory, direction: -1 | 1): Promise<void> {
    /* v8 ignore next 3 -- move controls are only rendered once an active list exists. */
    if (activeList === undefined) {
      return;
    }
    const ids = activeList.categories.map((item) => item.id);
    const index = ids.indexOf(category.id);
    const nextIndex = index + direction;
    /* v8 ignore next 3 -- boundary buttons are disabled before this handler can be called. */
    if (nextIndex < 0 || nextIndex >= ids.length) {
      return;
    }
    [ids[index], ids[nextIndex]] = [ids[nextIndex], ids[index]];
    setActiveList({
      ...activeList,
      categories: ids.flatMap((id, sortOrder) => {
        const reordered = activeList.categories.find((item) => item.id === id);
        return reordered === undefined ? [] : [{ ...reordered, sortOrder }];
      })
    });
    await reorderShoppingCategories(activeList.id, ids);
    await refreshList(activeList.id);
  }

  const completedCount = activeList?.items.filter((item) => item.completed).length ?? 0;

  return (
    <div className="shopping-panel">
      <header className="shopping-header">
        <div>
          <p className="eyebrow">Shopping</p>
          <h2>{isAllListsView ? "All shopping lists" : activeList?.name ?? "Shopping lists"}</h2>
        </div>
        <div className="shopping-header-actions">
          {activeList !== undefined && !isAllListsView ? (
            <button className="primary-action shopping-add-shortcut" type="button" aria-expanded={isAddingItem} aria-controls="shopping-add-form" onClick={() => { setIsAddingItem(!isAddingItem); }}>
              {isAddingItem ? "Cancel" : "Add new item"}
            </button>
          ) : null}
          <details className="shopping-menu">
            <summary className="secondary-action">List menu</summary>
            <div className="shopping-menu-panel">
              {activeList !== undefined && !isAllListsView ? (
                <>
                  <button className="secondary-action" type="button" aria-expanded={isBulkImporting} aria-controls="shopping-bulk-import-form" onClick={() => { setIsBulkImporting(!isBulkImporting); }}>
                    {isBulkImporting ? "Cancel bulk import" : "Bulk import items"}
                  </button>
                  <button className="secondary-action shopping-manage-categories" type="button" onClick={() => { setIsManagingCategories(!isManagingCategories); }}>
                    {isManagingCategories ? "Done managing categories" : "Manage categories"}
                  </button>
                </>
              ) : null}
              <button className="secondary-action" type="button" onClick={() => { onNavigate("/shopping-lists/all"); }}>All lists</button>
              <form className="shopping-create" onSubmit={(event) => { void onCreateList(event); }}>
                <input className="text-input" value={newListName} onChange={(event) => { setNewListName(event.target.value); }} aria-label="New list name" />
                <button className="secondary-action" type="submit">New list</button>
              </form>
            </div>
          </details>
        </div>
      </header>

      {error !== undefined ? <p className="error-text" role="alert">{error}</p> : null}
      {syncMessage !== "Synced" ? <p className="muted-text">{syncMessage}</p> : null}

      {isAllListsView ? (
        <section className="shopping-all-lists" aria-labelledby="shopping-all-lists-heading">
          <h3 id="shopping-all-lists-heading">All lists</h3>
          {lists.length > 0 ? (
            <div className="shopping-list-cards">
              {lists.map((list) => (
                <button
                  key={list.id}
                  className={list.id === activeList?.id ? "shopping-list-card active" : "shopping-list-card"}
                  type="button"
                  onClick={() => { onNavigate(`/shopping-lists/${list.id}`); }}
                >
                  <span>{list.name}</span>
                  <small>{list.activeItemCount} incomplete · {list.completedItemCount} completed</small>
                </button>
              ))}
            </div>
          ) : (
            <p className="muted-text">Create a named list to start shopping.</p>
          )}
        </section>
      ) : null}

      {activeList === undefined && !isAllListsView ? <p className="muted-text">Create a named list to start shopping.</p> : null}

      {activeList !== undefined && !isAllListsView ? (
        <>
          {isAddingItem ? (
            <form id="shopping-add-form" className="shopping-add" onSubmit={(event) => { void onAddItem(event); }}>
              <input
                id="shopping-item-name"
                ref={itemNameInputRef}
                className="text-input shopping-item-input"
                value={itemName}
                onChange={(event) => { setItemName(event.target.value); }}
                placeholder="Add item"
                aria-label="Item name"
                list="shopping-suggestions"
              />
              <input
                className="text-input shopping-qty-input"
                value={quantityText}
                onChange={(event) => { setQuantityText(event.target.value); }}
                placeholder="Qty"
                aria-label="Quantity"
              />
              <select className="text-input shopping-category-select" value={categoryId} onChange={(event) => { setCategoryId(event.target.value); }} aria-label="Category">
                {activeList.categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                <option value="__new__">New category…</option>
              </select>
              {categoryId === "__new__" ? (
                <input
                  className="text-input shopping-new-category-input"
                  value={newCategoryName}
                  onChange={(event) => { setNewCategoryName(event.target.value); }}
                  placeholder="Category name"
                  aria-label="New category name"
                />
              ) : null}
              <button className="primary-action shopping-add-button" type="submit">Add</button>
              <datalist id="shopping-suggestions">
                {suggestions.map((suggestion) => <option key={`${suggestion.canonicalName}-${suggestion.categoryId}`} value={suggestion.displayName} />)}
              </datalist>
            </form>
          ) : null}

          {isBulkImporting ? (
            <form id="shopping-bulk-import-form" className="shopping-bulk-import" onSubmit={(event) => { void onBulkImport(event); }}>
              <label className="field-label" htmlFor="shopping-bulk-import-text">Bulk import items</label>
              <textarea
                id="shopping-bulk-import-text"
                className="text-input shopping-bulk-import-text"
                value={bulkImportText}
                onChange={(event) => { setBulkImportText(event.target.value); }}
                placeholder={"Milk\nBread\nBananas"}
                rows={6}
              />
              <p className="muted-text">Each line becomes a new item. Cairn uses your category history first, then AI, then Uncategorised.</p>
              <button className="primary-action" type="submit">Import items</button>
            </form>
          ) : null}

          {isAddingItem && suggestions.length > 0 ? (
            <div className="shopping-suggestions" aria-label="Autocomplete suggestions">
              {suggestions.map((suggestion) => (
                <button
                  key={`${suggestion.canonicalName}-${suggestion.categoryId}`}
                  type="button"
                  onClick={() => {
                    setItemName(suggestion.displayName);
                    setCategoryId(suggestion.categoryId);
                  }}
                >
                  {suggestion.displayName} <span>{suggestion.categoryName}</span>
                </button>
              ))}
            </div>
          ) : null}

          {completedCount > 0 ? (
            <button className="secondary-action shopping-clear" type="button" onClick={() => { void onClearCompleted(); }}>
              Clear completed ({completedCount})
            </button>
          ) : null}

          <div className="shopping-categories">
            {groupedItems.map(({ category, items }, index) => (
              <section key={category.id} className="shopping-category">
                <header className="shopping-category-header">
                  <h3>{category.name}</h3>
                  {isManagingCategories ? (
                    <div className="shopping-category-actions" aria-label={`Manage ${category.name}`}>
                      <button type="button" aria-label={`Move ${category.name} up`} disabled={index === 0} onClick={() => { void moveCategory(category, -1); }}>↑</button>
                      <button type="button" aria-label={`Move ${category.name} down`} disabled={index === groupedItems.length - 1} onClick={() => { void moveCategory(category, 1); }}>↓</button>
                      <button type="button" className="danger-action" aria-label={`Delete ${category.name}`} disabled={groupedItems.length <= 1} onClick={() => { void onDeleteCategory(category); }}>Delete</button>
                    </div>
                  ) : null}
                </header>
                {items.length === 0 ? null : (
                  <ul className="shopping-items">
                    {items.map((item) => (
                      <li key={item.id}>
                        <button
                          className={item.completed ? "shopping-item completed" : "shopping-item"}
                          type="button"
                          aria-pressed={item.completed}
                          onClick={() => { void onToggleItem(item); }}
                        >
                          <span className="shopping-item-check">{item.completed ? "✓" : ""}</span>
                          <span className="shopping-item-main">
                            <span className="shopping-item-name">{item.displayName}</span>
                            <span className="shopping-item-meta">
                              {[item.quantityText, item.sourceRefs.map((source) => source.recipeTitle).filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
